/**
 * CaregiverAccessibilityPage (CONTRACTS §6): accessibility preferences for
 * the caregiver via repositories.profile settings. Display preferences are
 * applied to the document immediately (shared applier from the settings
 * workstream) and persisted per user — not in localStorage.
 */
import { useCallback, useEffect, useState } from 'react'
import { Card, SegmentedControl, Skeleton, Switch, useToast } from '@/components/ui'
import { repositories } from '@/services/repositories'
import { applyDisplayPreferences } from '@/features/settings/applyPreferences'
import { errorMessage } from '@/features/medication'
import type { UserSettings } from '@/types'
import './caregiver.css'

type PreferenceKey = 'reducedMotion' | 'highContrast' | 'darkMode' | 'haptics' | 'sounds' | 'navCollapsed'

const TEXT_SIZE_OPTIONS: { value: UserSettings['textSize']; label: string }[] = [
  { value: 'small', label: 'Klein' },
  { value: 'normal', label: 'Normaal' },
  { value: 'large', label: 'Groot' },
]

type PageState =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'ready'; settings: UserSettings }

export default function CaregiverAccessibilityPage() {
  const { showToast } = useToast()
  const [state, setState] = useState<PageState>({ status: 'loading' })
  const [savingKey, setSavingKey] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    repositories.profile
      .getSettings()
      .then((settings) => {
        if (cancelled) return
        if (settings) {
          setState({ status: 'ready', settings })
          applyDisplayPreferences(settings)
        } else {
          setState({ status: 'unavailable' })
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'unavailable' })
      })
    return () => {
      cancelled = true
    }
  }, [])

  const update = useCallback(
    async (key: PreferenceKey | 'textSize', value: boolean | UserSettings['textSize']) => {
      if (state.status !== 'ready' || savingKey) return
      const previous = state.settings
      const optimistic: UserSettings = { ...previous, [key]: value } as UserSettings
      setState({ status: 'ready', settings: optimistic })
      applyDisplayPreferences(optimistic)
      setSavingKey(key)
      try {
        const saved = await repositories.profile.upsertSettings({ [key]: value })
        setState({ status: 'ready', settings: saved })
        applyDisplayPreferences(saved)
      } catch (error) {
        setState({ status: 'ready', settings: previous })
        applyDisplayPreferences(previous)
        showToast({ message: errorMessage(error), tone: 'error' })
      } finally {
        setSavingKey(null)
      }
    },
    [state, savingKey, showToast],
  )

  return (
    <div className="caregiver-page">
      <header>
        <h1 className="caregiver-section-title">Toegankelijkheid</h1>
        <p className="caregiver-muted">
          Pas de weergave van de begeleidersomgeving aan. Dit geldt alleen voor jouw account.
        </p>
      </header>

      {state.status === 'loading' ? <Skeleton lines={4} /> : null}
      {state.status === 'unavailable' ? (
        <Card padding="md">
          <p className="caregiver-muted">
            Je voorkeuren kunnen nu niet worden opgehaald. Probeer het later opnieuw.
          </p>
        </Card>
      ) : null}

      {state.status === 'ready' ? (
        <Card padding="md">
          <div className="caregiver-form">
            <SegmentedControl
              label="Tekstgrootte"
              value={state.settings.textSize}
              onChange={(textSize) => void update('textSize', textSize as UserSettings['textSize'])}
              options={TEXT_SIZE_OPTIONS}
            />
            <Switch
              label="Verminderde beweging"
              description="Animaties en overgangen worden beperkt."
              checked={state.settings.reducedMotion}
              onChange={(checked) => void update('reducedMotion', checked)}
            />
            <Switch
              label="Hoog contrast"
              description="Sterkere contrasten voor tekst en randen."
              checked={state.settings.highContrast}
              onChange={(checked) => void update('highContrast', checked)}
            />
            <Switch
              label="Donkere weergave"
              description="Donker kleurenschema waar beschikbaar."
              checked={state.settings.darkMode}
              onChange={(checked) => void update('darkMode', checked)}
            />
            <Switch
              label="Trillen bij acties"
              description="Haptische feedback op apparaten die dat ondersteunen."
              checked={state.settings.haptics}
              onChange={(checked) => void update('haptics', checked)}
            />
            <Switch
              label="Geluiden"
              description="Korte geluidssignalen bij bevestigingen."
              checked={state.settings.sounds}
              onChange={(checked) => void update('sounds', checked)}
            />
            <Switch
              label="Menu standaard ingeklapt"
              description="De onderste navigatie start ingeklapt."
              checked={state.settings.navCollapsed}
              onChange={(checked) => void update('navCollapsed', checked)}
            />
          </div>
        </Card>
      ) : null}
    </div>
  )
}
